<template>
  <bootstrap-modal
    v-show="showModal"
    size="lg"
  >
    <div
      slot="header"
      class="modal-header"
    >
      <button
        type="button"
        class="close modal__close-button"
        data-dismiss="modal"
        aria-label="Close"
        @click="showModal = false"
      >
        <span
          aria-hidden="true"
          class="retouching-modal__close-icon"
        >&times;</span>
      </button>
      <h4 class="modal-title">
        <contentful-text :item="contentfulText('retouching-modal-title')"/>
      </h4>
    </div>
    <div
      slot="body"
      class="modal-body"
    >
      <p>
        <contentful-text :item="contentfulText('retouching-modal-lede')"/>
      </p>
      <div class="row">
        <div class="col-xs-12 col-sm-6 col-md-7">
          <div class="retouching-modal__retouching-packages">
            <div class="row">
              <retouching-package-detail
                v-for="retouchingPackage in viewModel.RetouchingPackages"
                :key="retouchingPackage.CatalogPackageID"
                :retouching-package="retouchingPackage"
                :is-selected="selectedRetouchingPackage.OfferPackageID === retouchingPackage.OfferPackageID"
                :total-number-retouching-packages="viewModel.RetouchingPackages.length"
                @retouching-package-click="handleRetouchingPackageClick(retouchingPackage)"
              />
            </div>
          </div>
        </div>
        <div class="col-xs-12 col-sm-6 col-md-5">
          <retouching-package-sidebar
            :retouching-package="selectedRetouchingPackage"
            :frame-id="frameId"
            :retouchings-in-cart="retouchingsInCart"
            :selected-retouchings="selectedRetouchings"
            :content="content"
            @retouching-added="handleRetouchingAdded"
            @retouching-removed="handleRetouchingRemoved"
          />
        </div>
      </div>
    </div>
    <div
      slot="footer"
      class="modal-footer"
    >
      <div class="row">
        <div class="col-xs-12 col-md-8">
          <div
            v-if="showDisplayOnFrameSelectToggle"
            class="retouchings__modal-optional"
          >
            <label class="v2-checkbox-label">
              <div class="v2-checkbox-wrapper">
                <input
                  v-model="alwaysShowRetouchingModal"
                  :aria-label="alwaysShowRetouchingModalCheckboxAriaLabel"
                  type="checkbox"
                  value
                  name
                >
                <div class="v2-checkbox"/>
              </div>
              <div class="v2-checkbox__text">
                <span class="hidden-md hidden-lg">
                  <contentful-text
                    :item="contentfulText('retouching-modal-enabled-checkbox-mobile')"
                  />
                </span>
                <span class="hidden-xs hidden-sm">
                  <contentful-text
                    :item="contentfulText('retouching-modal-enabled-checkbox-desktop')"
                  />
                </span>
              </div>
            </label>
          </div>
        </div>
        <div class="col-xs-12 col-md-4">
          <div class="retouchings__modal-action">
            <div v-if="addToCartMode">
              <button
                v-if="selectedRetouchings.length > 0"
                :aria-label="contentfulText('label-continue').Content"
                class="button retouching-modal__button--continue"
                type="submit"
                @click="handleAddToCartContinue"
              >
                <contentful-text :item="contentfulText('label-continue')"/>
              </button>
              <button
                v-if="selectedRetouchings.length === 0"
                :aria-label="contentfulText('label-skip-touchups').Content"
                class="button retouching-modal__button--skip"
                type="submit"
                @click="handleAddToCartContinue"
              >
                <contentful-text :item="contentfulText('label-skip-touchups')"/>
              </button>
            </div>
            <div v-if="!addToCartMode">
              <button
                v-if="selectedRetouchings.length === 0"
                :aria-label="contentfulText('label-skip-touchups').Content"
                class="button retouching-modal__button--skip"
                type="button"
                @click.prevent="showModal = false"
              >
                <contentful-text :item="contentfulText('label-skip-touchups')"/>
              </button>
              <button
                v-if="selectedRetouchings.length > 0"
                :aria-label="contentfulText('label-continue').Content"
                class="button retouching-modal__button--continue"
                type="button"
                @click.prevent="showModal = false"
              >
                <contentful-text :item="contentfulText('label-continue')"/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </bootstrap-modal>
</template>

<script>
import BootstrapModal from '@Shared/components/BootstrapModal'
import RetouchingPackageDetail from './RetouchingPackageDetail'
import RetouchingPackageSidebar from './RetouchingPackageSidebar'
import ContentfulMixin from '@Shared/mixins/contentful'
import ResponsiveBreakpointsMixin from '@Shared/mixins/responsive-breakpoints'
import GoogleAnalytics from '@Shared/lib/google-analytics'
import { findIndex, first } from 'lodash'

export default {
  name: 'RetouchingModal',
  components: {
    BootstrapModal,
    RetouchingPackageDetail,
    RetouchingPackageSidebar
  },
  mixins: [ContentfulMixin, ResponsiveBreakpointsMixin],
  props: {
    allowRetouching: { type: Boolean, default: false },
    viewModel: { type: Object, default: null },
    retouchingsInCart: {
      type: Object,
      default: null
    },
    content: {
      type: Object,
      default: null
    },
    selectedRetouchings: {
      type: Array,
      default: () => {
        return []
      }
    },
    showOncePerFrame: {
      type: Boolean,
      default: false
    },
    showDisplayOnFrameSelectToggle: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showModal: false,
      frameId: null,
      selectedRetouchingPackage: null,
      alwaysShowRetouchingModal: true,
      framesViewed: [],
      addToCartMode: false
    }
  },
  computed: {
    alwaysShowRetouchingModalCheckboxAriaLabel () {
      return this.isMobile ? this.contentfulText('retouching-modal-enabled-checkbox-mobile').Content : this.contentfulText('retouching-modal-enabled-checkbox-desktop').Content
    }
  },
  watch: {
    showModal (v, ov) {
      if (v === true) {
        this.selectedRetouchingPackage = first(
          this.viewModel.RetouchingPackages
        ) // reset to show the first retouching package when modal closes/hides

        this.framesViewed.push(this.frameId)
      }

      let gaEvent = v === true ? 'Retouching Modal Shown' : 'Closed Retouching Modal'
      GoogleAnalytics.trackEvent('Proof', gaEvent, null)
    },
    frameId (v, ov) {
      this.$emit('frame-changed')
    },
    alwaysShowRetouchingModal (v, ov) {
      this.$store.commit('toggleAlwaysShowRetouchingModal', v)
    }
  },
  created () {
    document.addEventListener('retouchingModal.show', ev => {
      this.frameId = parseInt(ev.detail.frameId, 10)

      if (
        this.showOncePerFrame === true &&
        (ev.detail.alwaysShow === undefined || ev.detail.alwaysShow === false) // this setting will override the showOncePerFrame functionality. used by the enhance button on each frame
      ) {
        this.showModal = this.framesViewed.indexOf(this.frameId) === -1 // only show the modal if the modal hasn't been shown for the frame already
      } else {
        this.showModal = true
      }

      if (ev.detail.addToCartMode !== undefined) {
        this.addToCartMode = ev.detail.addToCartMode // bool expected
      }
    })

    this.selectedRetouchingPackage = first(
      this.viewModel.RetouchingPackages
    )

    if (window.localStorage.getItem('alwaysShowRetouchingModal') === null) {
      window.localStorage.setItem('alwaysShowRetouchingModal', 'true')
    }

    this.alwaysShowRetouchingModal = window.localStorage.getItem('alwaysShowRetouchingModal') === 'true'
  },
  methods: {
    handleRetouchingAdded (data) {
      this.$emit('retouching-added', data)
    },
    handleRetouchingRemoved (data) {
      this.$emit('retouching-removed', data)
    },
    handleRetouchingPackageClick (retouchingPackage) {
      if (this.isMobile === true) {
        this.selectedRetouchingPackage = retouchingPackage
      } else {
        if (
          this.selectedRetouchingPackage.OfferPackageID ===
          retouchingPackage.OfferPackageID
        ) {
          this.selectNextOrFirstPackage()
        } else {
          this.selectedRetouchingPackage = retouchingPackage
        }
      }
    },
    selectNextOrFirstPackage () {
      let currentSelectedIndex = findIndex(
        this.viewModel.RetouchingPackages,
        { OfferPackageID: this.selectedRetouchingPackage.OfferPackageID }
      )

      if (
        this.viewModel.RetouchingPackages.length - 1 ===
        currentSelectedIndex
      ) {
        // if at the end of the array
        this.selectedRetouchingPackage = first(
          // select the first item
          this.viewModel.RetouchingPackages
        )
      } else {
        let nextIndex = currentSelectedIndex + 1
        this.selectedRetouchingPackage = this.viewModel.RetouchingPackages[nextIndex] // select the next item
      }
    },
    handleAddToCartContinue () {
      this.showModal = false
      document.dispatchEvent(new CustomEvent('package.addToCartFormSubmitted'))
    }
  }
}
</script>

<style lang="scss" scoped>
.retouching-modal {
  &__retouching-item {
    cursor: pointer;
  }

  &__button--skip {
    background-color: transparent;
    color: #b790b7;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
  }

  &__button--continue {
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    font-family: $font-open-sans;
  }

  &__retouching-packages {
    margin-bottom: 20px;
  }

  @media screen and (min-width: $screen-sm-min) {
    &__retouching-packages {
      margin-bottom: 0;
    }

    &__button--skip {
      font-size: 22px;
    }

    &__button--continue {
      font-size: 16px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/shared/components/catalog/RetouchingModal/RetouchingModal.vue